import type { Conversation, WorkspaceSurface, WorkspaceSurfaceSummary } from '@/types'
import type { ConversationMode } from '@/types/conversationRuntime'

export const WORKSPACE_SURFACES: Record<WorkspaceSurface, WorkspaceSurfaceSummary> = {
  chat: {
    id: 'chat',
    label: 'Samtale',
    description: 'Raske svar, ideer og avklaringer',
    runtime: 'chat',
    panel: 'closed',
  },
  agent: {
    id: 'agent',
    label: 'Arbeidsflyt',
    description: 'Utfør oppgaver, bygg, automatiser, gjør research og mer',
    runtime: 'agent',
    panel: 'agent',
  },
  // Studio and webdev are internal surfaces — auto-activated by the agent, not user-selectable.
  // They still exist so that side panels (StudioPanel, PagePanel) can open when needed.
  studio: {
    id: 'studio',
    label: 'Studio',
    description: 'Workflows, triggere og automatiseringer (aktivert av agent)',
    runtime: 'agent',
    panel: 'studio',
  },
  webdev: {
    id: 'webdev',
    label: 'Pages',
    description: 'Bygg og publiser nettsider med Sine Pages (aktivert av agent)',
    runtime: 'agent',
    panel: 'studio',
  },
}

export function getConversationSurface(conversation?: Conversation | null): WorkspaceSurface {
  if (!conversation) return 'chat'
  if (conversation.appMode === 'studio') return 'studio'
  if ((conversation.studioState?.interfaceBuilds?.length ?? 0) > 0) return 'studio'
  if (conversation.studioState?.activeInterfacePreview?.interfaceId) return 'studio'
  if (conversation.useAgentMode) return 'agent'
  return 'chat'
}

export type RuntimeFallbackTarget = 'chat' | 'agent'

export interface ConversationRuntimeSurface {
  surface: WorkspaceSurface
  routeOverride?: ConversationMode
  fallbackTarget: RuntimeFallbackTarget
  showAgentControls: boolean
  isStudioSplit: boolean
}

export function getConversationRuntimeSurface({
  appMode = 'chat',
  studioOpen = false,
  useAgentMode = false,
}: {
  appMode?: 'chat' | 'studio'
  studioOpen?: boolean
  useAgentMode?: boolean
}): ConversationRuntimeSurface {
  const isStudioSplit = appMode === 'studio' && studioOpen && !useAgentMode

  if (isStudioSplit) {
    return {
      surface: 'studio',
      routeOverride: 'studio_build',
      fallbackTarget: 'agent',
      showAgentControls: false,
      isStudioSplit: true,
    }
  }

  if (useAgentMode) {
    return {
      surface: 'agent',
      routeOverride: 'agent_task',
      fallbackTarget: 'agent',
      showAgentControls: true,
      isStudioSplit: false,
    }
  }

  return {
    surface: 'chat',
    fallbackTarget: 'chat',
    showAgentControls: false,
    isStudioSplit: false,
  }
}

export function isWebdevSurface(surface: WorkspaceSurface): boolean {
  return surface === 'webdev'
}

export function buildRuntimeInstruction(task: string, surface: WorkspaceSurface): string {
  const trimmedTask = task.trim()
  if (!trimmedTask) return task

  if (surface === 'chat') {
    return trimmedTask
  }

  const sharedRules = [
    'Vurder om du har nok informasjon til å starte umiddelbart.',
    'Hvis kritisk informasjon mangler, still korte og konkrete avklaringsspørsmål før du handler.',
    'Hvis du kan starte trygt, start med en gang uten unødvendig friksjon.',
    'Hvis noe feiler, forklar tydelig hva som feilet og foreslå neste beste steg, inkludert takeover eller manuell hjelp ved behov.',
  ]

  // Unified runtime — all deep-work surfaces use the same instructions.
  // Sine automatically decides which capabilities to use based on the task.
  return [
    '[SINE WORKFLOW RUNTIME]',
    'Du jobber i Sines dype arbeidsflyt. Du er en autonom digital arbeidskraft som kan utføre komplekse oppgaver med verktøy, filer, nettleser og terminal.',
    '',
    '## Dine capabilities',
    '- **Direkte agentisk arbeid**: Kjør kommandoer, les/skriv filer, søk på nett, styr nettleser',
    '- **Nettsider og webapper (Sine Pages)**: Bygg og publiser nettsider. Bruk byggeplanen for å avklare innholdet, bygg steg for steg, og deploy automatisk.',
    '- **Studio og automatisering**: Bygg workflows, triggere og automatiseringer. Kartlegg systemer, lag gjenbrukbare profiler.',
    '- **Connectors**: Bruk tilkoblede tjenester (Gmail, Meta Ads, GitHub, Instagram, etc.) til å utføre handlinger.',
    '',
    '## Regler',
    ...sharedRules,
    'Bruk korte statusoppdateringer underveis og vær tydelig på fremdrift, blokkeringer og resultat.',
    'Hvis conversation_history er tilgjengelig, bruk den til å forstå hva som har skjedd tidligere i samtalen.',
    'Hvis brukeren spør om noe som stoppet, feilet eller ikke skjedde — les samtalehistorikken og fortsett oppgaven.',
    'Ikke be om mer kontekst hvis samtalehistorikken allerede inneholder oppgaven — bare fortsett.',
    '',
    '## Nettside-bygging',
    'Når brukeren vil bygge en nettside, webapp eller landingsside:',
    '- Bruk byggeplanen for å avklare prosjektet før bygging',
    '- Bygg siden steg for steg og vis fremdrift',
    '- Fokuser på design, responsivitet og brukervennlighet',
    '- Deploy automatisk til Cloudflare Pages via GitHub',
    '',
    '## Automatisering og workflows',
    'Når brukeren vil automatisere noe eller sette opp workflows:',
    '- Kartlegg systemet først (login, navigasjon, steg)',
    '- Lag gjenbrukbare automation profiles',
    '- Sett opp triggers for repeterende oppgaver',
    '- Gjenbruk eksisterende profiler når mulig',
    '',
    'Brukeroppgave:',
    trimmedTask,
  ].join('\n')
}
